import React, { useEffect, useRef } from 'react';
import TaskDetailCore from './TaskDetailCore';
import './TaskDetailSidePanel.css';

/**
 * TaskDetailSidePanel - Side panel wrapper for task details
 * Used when tasks are opened from Backlog
 *
 * Supports both old (taskId) and new (identifier/identifierType) props
 *
 * Features:
 * - Right-docked panel
 * - Lighter backdrop (backlog remains visible)
 * - ESC key to close
 * - Click outside to close
 * - Browser back button closes panel
 * - Smooth slide-in animation
 */
const TaskDetailSidePanel = ({ taskId, identifier, identifierType = 'id', onClose, onDeleted }) => {
  const panelRef = useRef(null);

  // Support both old and new props
  const effectiveTaskId = identifier || taskId;

  // Prevent body scroll and compensate for scrollbar width to avoid layout shift
  useEffect(() => {
    // Calculate scrollbar width before hiding it
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Add padding to compensate for scrollbar removal (prevents layout shift)
    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }

    return () => {
      // Restore body scroll and remove padding
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    };
  }, []);

  // ESC key handling
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      onClose();
    };

    // Push a dummy state so back button can be intercepted
    window.history.pushState({ sidePanelOpen: true }, '');

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [onClose]);

  return (
    <>
      {/* Backdrop overlay (lighter than modal) */}
      <div className="side-panel-backdrop" onClick={onClose} />

      {/* Side Panel */}
      <div
        ref={panelRef}
        className="task-side-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="task-panel-title"
      >
        {/* Close button */}
        <button
          className="side-panel-close"
          onClick={onClose}
          aria-label="Close panel"
          title="Close (ESC)"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
          </svg>
        </button>

        {/* Scrollable content */}
        <div className="side-panel-content">
          <TaskDetailCore
            identifier={effectiveTaskId}
            identifierType={identifierType}
            taskId={taskId}
            renderMode="sidepanel"
            onClose={onClose}
            onDeleted={onDeleted}
          />
        </div>
      </div>
    </>
  );
};

export default TaskDetailSidePanel;
